/**
 * Risk Calculation Utilities
 * 
 * Client-side risk assessment logic for initiatives.
 * Mirrors backend logic for real-time filtering and display.
 * 
 * IMPORTANT: Velocity-based risk checks ONLY apply in the final 2 sprints
 * to prevent false positives early in the initiative lifecycle.
 */

import { getCurrentSprint } from './dateUtils'

/**
 * Risk thresholds configuration
 * Should match backend configuration
 */
export interface RiskThresholds {
  sprintsRemainingThreshold: number
  openStoryPointsPercentage: number
  openStoryPointsAbsolute: number
  storyPointsPerSprintPercentage: number
}

/**
 * Default risk thresholds
 * Matches backend/src/config/riskThresholds.js
 */
export const DEFAULT_RISK_THRESHOLDS: RiskThresholds = {
  sprintsRemainingThreshold: 2,
  openStoryPointsPercentage: 15,
  openStoryPointsAbsolute: 20,
  storyPointsPerSprintPercentage: 20
}

/**
 * Risk assessment result
 */
export interface RiskAssessment {
  atRisk: boolean
  riskReasons: string[]
  metrics: {
    remainingSprints: number
    remainingSP: number
    openPercentage: number
    totalSP: number
    completedSP: number
    requiredVelocity?: number
    velocityThreshold?: number
    isInFinalTwoSprints?: boolean
  }
}

/**
 * Initiative interface for risk calculation
 */
export interface Initiative {
  totalStoryPoints?: number
  completedStoryPoints?: number
  endsAtSprint?: number
  endDate?: string | null
  status?: string
  [key: string]: any
}

/**
 * Calculate remaining sprints for an initiative
 */
export function calculateRemainingSprints(
  endsAtSprint: number | undefined,
  currentSprint: number
): number {
  if (!endsAtSprint || !currentSprint) {
    return 0
  }

  const remaining = endsAtSprint - currentSprint
  return Math.max(0, remaining)
}

/**
 * Calculate remaining story points for an initiative
 */
export function calculateRemainingStoryPoints(
  totalStoryPoints: number | undefined,
  completedStoryPoints: number | undefined
): number {
  if (!totalStoryPoints || totalStoryPoints === 0) {
    return 0
  }

  const completed = completedStoryPoints || 0
  const remaining = totalStoryPoints - completed
  return Math.max(0, remaining)
}

/**
 * Check if an initiative is overdue (end date/sprint is in the past)
 *
 * @param endDate - The end date of the initiative (ISO string format)
 * @param endsAtSprint - The sprint number when the initiative ends
 * @param currentSprint - The current sprint number
 * @returns true if the initiative is overdue, false otherwise
 */
export function isInitiativeOverdue(
  endDate: string | null | undefined,
  endsAtSprint?: number,
  currentSprint?: number
): boolean {
  // Check if sprint-based timeline indicates overdue
  if (endsAtSprint && currentSprint) {
    if (endsAtSprint < currentSprint) {
      return true // Sprint has passed, so it's overdue
    }
  }

  // Check if date-based timeline indicates overdue
  if (!endDate) {
    return false // No end date means not overdue (unless sprint check above caught it)
  }

  try {
    const today = new Date()
    today.setHours(0, 0, 0, 0) // Set to midnight for date-only comparison

    const end = new Date(endDate)
    end.setHours(0, 0, 0, 0) // Set to midnight for date-only comparison

    return end < today // Overdue if end date is before today
  } catch (error) {
    console.error('Error parsing end date:', endDate, error)
    return false // If we can't parse the date, assume not overdue
  }
}

/**
 * Check if initiative is in its final 2 sprints
 *
 * IMPORTANT: This is used to determine if velocity-based risk should be applied.
 * Velocity checks ONLY run in the final 2 sprints to prevent false positives.
 */
export function isInFinalTwoSprints(
  currentSprint: number,
  endsAtSprint: number | undefined
): boolean {
  if (!currentSprint || !endsAtSprint) {
    return false
  }

  // Check if current sprint >= (ends at sprint - 1)
  // Example: Sprint 194 of 195 → 194 >= (195 - 1) = 194 >= 194 → true
  return currentSprint >= (endsAtSprint - 1)
}

/**
 * Assess if an initiative is at risk
 * 
 * Applies three risk conditions:
 * 1. Time-based percentage: ≤2 sprints remaining AND >15% work open
 * 2. Time-based absolute: ≤2 sprints remaining AND >20 SP open
 * 3. Velocity-based: In final 2 sprints AND required velocity > 20% of total scope
 */
export function assessInitiativeRisk(
  initiative: Initiative,
  currentSprint?: number,
  thresholds: RiskThresholds = DEFAULT_RISK_THRESHOLDS
): RiskAssessment {
  const result: RiskAssessment = {
    atRisk: false,
    riskReasons: [],
    metrics: {
      remainingSprints: 0,
      remainingSP: 0,
      openPercentage: 0,
      totalSP: 0,
      completedSP: 0
    }
  }

  // Get current sprint if not provided
  if (!currentSprint) {
    currentSprint = getCurrentSprint()
  }

  // Extract initiative data
  // Support both direct fields and nested customFields structure
  const totalSP = initiative.totalStoryPoints || 0
  const completedSP = initiative.completedStoryPoints || 0

  // Extract endsAtSprint from multiple possible locations
  let endsAtSprint: number | undefined = undefined
  if (initiative.endsAtSprint) {
    endsAtSprint = typeof initiative.endsAtSprint === 'number'
      ? initiative.endsAtSprint
      : parseInt(initiative.endsAtSprint, 10)
  } else if (initiative.customFields?.endsAtSprint) {
    const value = initiative.customFields.endsAtSprint
    if (typeof value === 'string' && value.trim() !== '') {
      endsAtSprint = parseInt(value, 10)
    } else if (typeof value === 'number') {
      endsAtSprint = value
    }
  }

  // Calculate derived metrics
  const remainingSprints = calculateRemainingSprints(endsAtSprint, currentSprint)
  const remainingSP = calculateRemainingStoryPoints(totalSP, completedSP)
  const openPercentage = totalSP > 0 ? (remainingSP / totalSP) * 100 : 0

  // Store metrics
  result.metrics = {
    remainingSprints,
    remainingSP,
    openPercentage: Math.round(openPercentage * 10) / 10,
    totalSP,
    completedSP
  }

  // Debug logging for first few initiatives
  if (Math.random() < 0.01) { // Log ~1% of initiatives to avoid spam
    console.log('🎯 Risk Assessment Debug:', {
      key: initiative.key,
      status: initiative.status,
      totalSP,
      completedSP,
      remainingSP,
      endsAtSprint,
      currentSprint,
      remainingSprints,
      openPercentage: Math.round(openPercentage),
      hasTimeline: !!endsAtSprint,
      hasWork: totalSP > 0,
      endDate: initiative.endDate,
      isOverdue: isInitiativeOverdue(initiative.endDate, endsAtSprint, currentSprint)
    })
  }

  // EXCLUDE PAUSED AND AWAITING VERIFICATION: These are handled by other cards
  const status = initiative.status?.toLowerCase() || ''
  if (status === 'paused' || status === 'awaiting verification') {
    return result // Not at risk because it's paused or awaiting verification
  }

  // EXCLUDE OVERDUE INITIATIVES: These are handled by the "Overdue Initiatives" card
  // Only assess risk for initiatives with future end dates or no end date
  if (isInitiativeOverdue(initiative.endDate, endsAtSprint, currentSprint)) {
    return result // Not at risk because it's already overdue
  }

  // Skip risk assessment if no timeline or no work defined
  if (!endsAtSprint || totalSP === 0) {
    return result
  }

  // Condition 1A: Time-based percentage risk
  if (remainingSprints <= thresholds.sprintsRemainingThreshold) {
    if (openPercentage > thresholds.openStoryPointsPercentage) {
      result.atRisk = true
      result.riskReasons.push(
        `${Math.round(openPercentage)}% work remaining with ${remainingSprints} sprint(s) left`
      )
    }
  }

  // Condition 1B: Time-based absolute risk
  if (remainingSprints <= thresholds.sprintsRemainingThreshold) {
    if (remainingSP > thresholds.openStoryPointsAbsolute) {
      result.atRisk = true
      result.riskReasons.push(
        `${remainingSP} SP remaining with ${remainingSprints} sprint(s) left`
      )
    }
  }

  // Condition 2: Velocity-based risk (ONLY in final 2 sprints)
  if (remainingSprints > 0) {
    const isInFinalTwo = isInFinalTwoSprints(currentSprint, endsAtSprint)
    
    if (isInFinalTwo) {
      const requiredVelocity = remainingSP / remainingSprints
      const velocityThreshold = (totalSP * thresholds.storyPointsPerSprintPercentage) / 100
      
      result.metrics.requiredVelocity = Math.round(requiredVelocity * 10) / 10
      result.metrics.velocityThreshold = Math.round(velocityThreshold * 10) / 10
      result.metrics.isInFinalTwoSprints = true
      
      if (requiredVelocity > velocityThreshold) {
        result.atRisk = true
        result.riskReasons.push(
          `High velocity required: ${Math.round(requiredVelocity)} SP/sprint (threshold: ${Math.round(velocityThreshold)})`
        )
      }
    } else {
      result.metrics.isInFinalTwoSprints = false
    }
  }

  return result
}

/**
 * Get all at-risk initiatives from a list
 */
export function getAtRiskInitiatives(
  initiatives: Initiative[],
  currentSprint?: number,
  thresholds: RiskThresholds = DEFAULT_RISK_THRESHOLDS
): Initiative[] {
  // Get current sprint if not provided
  if (!currentSprint) {
    currentSprint = getCurrentSprint()
  }

  const atRiskInitiatives: Initiative[] = []

  initiatives.forEach(initiative => {
    const assessment = assessInitiativeRisk(initiative, currentSprint, thresholds)
    
    if (assessment.atRisk) {
      atRiskInitiatives.push({
        ...initiative,
        riskAssessment: assessment
      })
    }
  })

  return atRiskInitiatives
}

/**
 * Get risk statistics for a list of initiatives
 */
export function getRiskStatistics(
  initiatives: Initiative[],
  currentSprint?: number,
  thresholds: RiskThresholds = DEFAULT_RISK_THRESHOLDS
) {
  // Get current sprint if not provided
  if (!currentSprint) {
    currentSprint = getCurrentSprint()
  }

  const stats = {
    total: initiatives.length,
    atRisk: 0,
    byReason: {
      timeBasedPercentage: 0,
      timeBasedAbsolute: 0,
      velocityBased: 0
    },
    currentSprint
  }

  initiatives.forEach(initiative => {
    const assessment = assessInitiativeRisk(initiative, currentSprint, thresholds)
    
    if (assessment.atRisk) {
      stats.atRisk++
      
      // Count by reason type
      assessment.riskReasons.forEach(reason => {
        if (reason.includes('% work remaining')) {
          stats.byReason.timeBasedPercentage++
        }
        if (reason.includes('SP remaining')) {
          stats.byReason.timeBasedAbsolute++
        }
        if (reason.includes('velocity required')) {
          stats.byReason.velocityBased++
        }
      })
    }
  })

  return stats
}

/**
 * Check if a single initiative is at risk (convenience function)
 */
export function isInitiativeAtRisk(
  initiative: Initiative,
  currentSprint?: number,
  thresholds: RiskThresholds = DEFAULT_RISK_THRESHOLDS
): boolean {
  const assessment = assessInitiativeRisk(initiative, currentSprint, thresholds)
  return assessment.atRisk
}

